import React from 'react';
import { AlertCircle, CheckCircle2, Calendar, User, Building2, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';

interface Client {
  id: string;
  name: string;
  company_name: string;
}

interface Sdr {
  user_id: string;
  sales_name: string | null;
  email: string | null;
}

interface ValidationError {
  type: 'error' | 'warning';
  message: string;
}

interface ValidationSummaryProps {
  dates: string[];
  clientId: string;
  sdrId?: string;
  sdrIds?: string[];
  clients: Client[];
  sdrs: Sdr[];
  dayType: string;
  errors: ValidationError[];
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const ValidationSummary: React.FC<ValidationSummaryProps> = ({
  dates,
  clientId,
  sdrId = '',
  sdrIds = [],
  clients,
  sdrs,
  dayType,
  errors,
  onConfirm,
  onCancel,
  isLoading = false
}) => {
  const client = clients.find(c => c.id === clientId);
  const sdr = sdrs.find(s => s.user_id === sdrId);
  const selectedSdrs = sdrIds.length > 0 ? sdrs.filter(s => sdrIds.includes(s.user_id)) : [];

  const hasErrors = errors.some(e => e.type === 'error');
  const hasWarnings = errors.some(e => e.type === 'warning');
  const totalEntries = dates.length * (sdrIds.length > 0 ? sdrIds.length : 1);

  const dayTypeLabels: Record<string, string> = {
    prospection: 'Prospection',
    malade: 'Maladie',
    congé: 'Congé',
    férié: 'Férié',
    formation: 'Formation'
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      weekday: 'long'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-750 rounded-lg border border-gray-600 p-6 space-y-6"
    >
      <div className="flex items-center space-x-3">
        <div className={`
          w-10 h-10 rounded-full flex items-center justify-center
          ${hasErrors ? 'bg-red-500/20' : hasWarnings ? 'bg-orange-500/20' : 'bg-green-500/20'}
        `}>
          {hasErrors ? (
            <AlertCircle className="h-5 w-5 text-red-400" />
          ) : hasWarnings ? (
            <AlertTriangle className="h-5 w-5 text-orange-400" />
          ) : (
            <CheckCircle2 className="h-5 w-5 text-green-400" />
          )}
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">
            {hasErrors ? 'Validation échouée' : hasWarnings ? 'Attention' : 'Validation réussie'}
          </h3>
          <p className="text-sm text-gray-400">
            {totalEntries} entrée{totalEntries > 1 ? 's' : ''} à créer
            {sdrIds.length > 0 && ` (${dates.length} date${dates.length > 1 ? 's' : ''} × ${sdrIds.length} SDR${sdrIds.length > 1 ? 's' : ''})`}
          </p>
        </div>
      </div>

      {(errors.length > 0) && (
        <div className="space-y-2">
          {errors.map((error, index) => (
            <div
              key={index}
              className={`
                flex items-start space-x-3 p-3 rounded-lg border
                ${error.type === 'error'
                  ? 'bg-red-500/10 border-red-500/30'
                  : 'bg-orange-500/10 border-orange-500/30'
                }
              `}
            >
              {error.type === 'error' ? (
                <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-orange-400 flex-shrink-0 mt-0.5" />
              )}
              <p className={`text-sm ${error.type === 'error' ? 'text-red-300' : 'text-orange-300'}`}>
                {error.message}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-4">
        <div className="bg-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-white mb-3">Détails de la création</h4>
          <div className="space-y-3">
            {client && (
              <div className="flex items-center space-x-3">
                <Building2 className="h-4 w-4 text-blue-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-400">Client</p>
                  <p className="text-sm text-white font-medium truncate">{client.name}</p>
                  <p className="text-xs text-gray-400 truncate">{client.company_name}</p>
                </div>
              </div>
            )}

            {(sdr || selectedSdrs.length > 0) && (
              <div className="flex items-center space-x-3">
                <User className="h-4 w-4 text-green-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-400">SDR{selectedSdrs.length > 1 ? 's' : ''} assigné{selectedSdrs.length > 1 ? 's' : ''}</p>
                  {selectedSdrs.length > 0 ? (
                    <div className="space-y-1">
                      {selectedSdrs.map(s => (
                        <p key={s.user_id} className="text-sm text-white font-medium truncate">
                          {s.sales_name || s.email || 'SDR'}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-white font-medium truncate">
                      {sdr?.sales_name || sdr?.email || 'SDR'}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center space-x-3">
              <Calendar className="h-4 w-4 text-purple-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-400">Type de journée</p>
                <p className="text-sm text-white font-medium">{dayTypeLabels[dayType] || dayType}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-white">Dates sélectionnées</h4>
            <span className="text-xs text-gray-400">{dates.length} date{dates.length > 1 ? 's' : ''}</span>
          </div>
          <div className="max-h-40 overflow-y-auto space-y-2">
            {dates.map(date => (
              <div
                key={date}
                className="text-xs text-gray-300 py-1.5 px-3 bg-gray-650 rounded"
              >
                {formatDate(date)}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-3 pt-4 border-t border-gray-600">
        <button
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Annuler
        </button>
        <button
          onClick={onConfirm}
          disabled={isLoading || hasErrors}
          className={`
            flex-1 px-4 py-2.5 rounded-lg font-medium transition-colors
            ${hasErrors
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 hover:bg-blue-700 text-white'
            }
            ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {isLoading ? (
            <span className="flex items-center justify-center space-x-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <span>Création en cours...</span>
            </span>
          ) : (
            `Créer ${dates.length} journée${dates.length > 1 ? 's' : ''}`
          )}
        </button>
      </div>
    </motion.div>
  );
};

export default ValidationSummary;
